import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Marquee } from "@/components/Marquee";
import { EDITORIAL, CAMPAIGNS, DEPARTMENTS, COLLECTIONS, deptLabel } from "@/data/site";
import { ArrowRight, Flame } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

export default function Home() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [filter, setFilter] = useState("all");
  const [heroIdx, setHeroIdx] = useState(0);

  useSeo({
    title: "HYEAMAN (Street Sheriff) — Wear Confidence · No One Is Left Behind",
    description: "HYEAMAN, also known as Street Sheriff — luxury editorial streetwear crowned in gold. Wear Confidence. No One Is Left Behind. Shop the latest HYEAMAN drops.",
    path: "/",
  });

  useEffect(() => {
    api.get("/products", { params: { is_new: true, limit: 8 } }).then((r) => setNewArrivals(r.data));
    api.get("/products", { params: { featured: true, limit: 12 } }).then((r) => setFeatured(r.data));
    api.get("/trending", { params: { limit: 4 } }).then((r) => setTrending(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % CAMPAIGNS.length), 5500);
    return () => clearInterval(t);
  }, []);

  const filtered = filter === "all" ? featured : featured.filter((p) => p.collection === filter);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0" data-testid="hero-campaign-rotator">
          {CAMPAIGNS.map((src, i) => (
            <img key={src} src={src} alt="HYEAMAN campaign" aria-hidden={i !== heroIdx}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${i === heroIdx ? "opacity-100 hero-zoom" : "opacity-0"}`} />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/70" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-20 sm:px-8 lg:px-12">
          <p className="overline-label reveal mb-4">Autumn Campaign · Volume XII</p>
          <h1 className="reveal font-black uppercase leading-[0.9] tracking-tight text-cream text-5xl sm:text-7xl lg:text-8xl" style={{ animationDelay: "80ms" }}>
            Wear<br /><span className="font-serif font-normal italic text-gradient-gold">Confidence</span>
          </h1>
          <p className="reveal mt-6 max-w-md text-cream/70" style={{ animationDelay: "160ms" }}>
            Editorial streetwear crowned in gold. Engineered for those built for greatness.
          </p>
          <p className="reveal mt-4 font-mono text-[0.7rem] uppercase tracking-[0.35em] text-gold/70" style={{ animationDelay: "200ms" }} data-testid="hero-slogans">
            Wear Confidence · No One Is Left Behind
          </p>
          <div className="reveal mt-8 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
            <Link to="/shop" data-testid="hero-cta-shop" className="bg-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">Shop the Collection</Link>
            <Link to="/shop?collection=black-label" data-testid="hero-cta-blacklabel" className="border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-widest text-cream transition hover:bg-gold/10">Black Label</Link>
          </div>
          <div className="mt-10 flex gap-2" data-testid="hero-dots">
            {CAMPAIGNS.map((_, i) => (
              <button key={i} onClick={() => setHeroIdx(i)} aria-label={`Campaign ${i + 1}`} data-testid={`hero-dot-${i}`}
                className={`h-1 transition-all duration-300 ${i === heroIdx ? "w-8 bg-gold" : "w-4 bg-cream/30 hover:bg-cream/60"}`} />
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* DROP PANELS */}
      <section className="mx-auto grid max-w-7xl gap-4 px-4 py-16 sm:px-8 md:grid-cols-2 lg:px-12">
        {[{ img: EDITORIAL.goldJacketMan, kicker: "Limited Drop", title: "The Gold Standard", to: "/shop?collection=elite" },
          { img: EDITORIAL.female, kicker: "Just Landed", title: "Ladies Editorial", to: "/shop?department=ladies" }].map((d, i) => (
          <Link key={d.title} to={d.to} className="group relative aspect-[16/11] overflow-hidden bg-ink" data-testid={`drop-panel-${i}`}>
            <img src={d.img} alt={d.title} className="h-full w-full object-cover product-img-hover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="overline-label mb-1">{d.kicker}</p>
              <h3 className="font-serif text-2xl text-cream">{d.title}</h3>
              <span className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold">Discover <ArrowRight size={14} /></span>
            </div>
          </Link>
        ))}
      </section>

      {/* NEW ARRIVALS */}
      <Section title="New Arrivals" kicker="Fresh from the atelier" link="/shop?sort=newest">
        <Grid>{newArrivals.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</Grid>
      </Section>

      {/* TRENDING */}
      {trending.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-8 lg:px-12" data-testid="trending-section">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <p className="overline-label mb-2 flex items-center gap-2"><Flame size={14} className="text-gold" /> Trending this week</p>
              <h2 className="font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">Most Wanted</h2>
            </div>
            <Link to="/shop" className="hidden shrink-0 items-center gap-2 text-xs uppercase tracking-widest text-gold hover:text-gold-bright sm:inline-flex">View all <ArrowRight size={14} /></Link>
          </div>
          <Grid>{trending.map((p, i) => <ProductCard key={p.id} product={p} index={i} ribbon="Trending" />)}</Grid>
        </section>
      )}

      {/* DEPARTMENTS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12">
        <Kicker text="Shop by department" title="Five Houses. One Crown." />
        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-5">
          {DEPARTMENTS.map((d, i) => (
            <Link key={d.key} to={`/shop?department=${d.key}`} data-testid={`department-${d.key}`}
              className="group relative flex aspect-[3/4] items-end overflow-hidden border border-white/10 bg-ink p-4 transition hover:border-gold/40">
              <img src={[EDITORIAL.tieMan, EDITORIAL.female, EDITORIAL.darkPortrait, EDITORIAL.goldJacketMan, EDITORIAL.blackLabel][i]} alt={d.label} className="absolute inset-0 h-full w-full object-cover opacity-50 transition group-hover:opacity-70 product-img-hover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <span className="relative font-serif text-lg text-cream group-hover:text-gold">{d.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* BLACK LABEL FEATURE */}
      <section className="relative overflow-hidden border-y border-gold/20" data-testid="black-label-feature">
        <img src={EDITORIAL.blackLabel} alt="Black Label" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-24 sm:px-8 lg:grid-cols-2 lg:px-12">
          <div>
            <p className="overline-label mb-3">The Pinnacle</p>
            <h2 className="font-black uppercase leading-none tracking-tight text-cream text-4xl sm:text-5xl">Black <span className="font-serif font-normal italic text-gold">Label</span></h2>
            <p className="mt-6 max-w-md text-cream/70">Numbered, limited and uncompromising. Our most refined pieces, finished by hand with 24k gold-tone detailing.</p>
            <Link to="/shop?collection=black-label" className="mt-8 inline-block border border-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-gold transition hover:bg-gold hover:text-black">Enter Black Label</Link>
          </div>
        </div>
      </section>

      {/* FILTERABLE FEATURED EDIT */}
      <Section title="The Featured Edit" kicker="Curated by HYEAMAN">
        <div className="mb-8 flex flex-wrap gap-2" data-testid="featured-filters">
          {[{ key: "all", label: "All" }, ...COLLECTIONS].map((c) => (
            <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`filter-${c.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${filter === c.key ? "border-gold bg-gold text-black" : "border-white/15 text-cream/70 hover:border-gold/50"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <Grid>{filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</Grid>
      </Section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-8">
        <p className="overline-label mb-6">Brand Manifesto</p>
        <p className="font-serif text-2xl italic leading-relaxed text-cream sm:text-3xl">
          "We do not follow the culture — we crown it. Every stitch is a statement, every piece a reminder that greatness is not given. It is <span className="text-gold">worn</span>."
        </p>
        <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-gold/70" data-testid="manifesto-brand">
          HYEAMAN, also known as Street Sheriff · Wear Confidence · No One Is Left Behind
        </p>
        <Link to="/street-sheriff" data-testid="home-ss-link" className="mt-8 inline-block border border-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-gold transition hover:bg-gold hover:text-black">Explore Street Sheriff</Link>
      </section>

      {/* COLLECTIONS STRIP */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-8 lg:px-12">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {COLLECTIONS.map((c) => (
            <Link key={c.key} to={`/shop?collection=${c.key}`} data-testid={`collection-${c.key}`}
              className="group border border-white/10 bg-ink p-6 transition hover:border-gold/40">
              <h3 className="font-serif text-xl text-cream group-hover:text-gold">{c.label}</h3>
              <p className="mt-2 text-sm text-cream/50">{c.note}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold opacity-0 transition group-hover:opacity-100">Shop <ArrowRight size={13} /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="border-y border-white/10 bg-ink">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-12 sm:px-8 md:grid-cols-4 lg:px-12">
          {[["Free Shipping", "On orders over C$250"], ["Signature Packaging", "Gift-ready, every time"], ["Secure Checkout", "Encrypted & protected"], ["Crafted in Detail", "Considered, not mass-made"]].map(([t, s]) => (
            <div key={t}>
              <p className="font-serif text-base text-gold">{t}</p>
              <p className="mt-1 text-xs text-cream/50">{s}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Section({ title, kicker, link, children }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12">
      <div className="mb-10 flex items-end justify-between">
        <Kicker text={kicker} title={title} />
        {link && <Link to={link} className="hidden shrink-0 items-center gap-2 text-xs uppercase tracking-widest text-gold hover:text-gold-bright sm:inline-flex">View all <ArrowRight size={14} /></Link>}
      </div>
      {children}
    </section>
  );
}
function Kicker({ text, title }) {
  return (
    <div>
      <p className="overline-label mb-2">{text}</p>
      <h2 className="font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">{title}</h2>
    </div>
  );
}
export function Grid({ children }) {
  return <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">{children}</div>;
}
